// frontend/src/services/terminalService.js
import { terminalAPI } from "./api";
import { API_CONFIG, AUTH_CONFIG } from "../config";
import { generateSmartDescription } from "../utils/posUtils";

/**
 * Terminal Service
 *
 * Provides a layer of abstraction over the terminal API with:
 * - Simplified direct API calls
 * - Improved error handling
 * - Consistent response formatting
 */
const terminalService = {
  // Display cart on terminal with direct approach (no queue)
  displayCart: async (displayData) => {
    const {
      readerId,
      cart,
      totals,
      customerName,
      prompt = "Confirm & Pay",
    } = displayData;

    if (!readerId) {
      return { success: false, error: "Reader ID is required" };
    }

    if (!cart || !Array.isArray(cart)) {
      return { success: false, error: "Cart must be an array" };
    }

    try {
      // An empty cart clears the reader. (The old code sent a fake
      // one-cent line that Stripe always rejected, so the reader kept the
      // last cart.)
      if (cart.length === 0) {
        return terminalService.clearDisplay(readerId);
      }

      // Ensure totals are valid and meet Stripe's minimum requirements
      const validatedTotals = {
        subtotal: Math.max(1, totals.subtotal || 0), // Ensure at least 1 cent
        taxAmount: totals.taxAmount || 0,
        total: Math.max(1, totals.total || 0), // Ensure at least 1 cent
      };

      // No wipe first: a new cart display replaces the one on the reader.
      // (The old fake "Updating display..." line was always rejected.)

      // Display cart on terminal with retry logic
      let retryCount = 0;
      const maxRetries = 2;

      while (retryCount <= maxRetries) {
        try {
          // Log the exact payload being sent to the terminal
          const payload = {
            readerId,
            cart,
            totals: validatedTotals,
            prompt,
            customerName,
          };

          const response = await terminalAPI.displayCartOnTerminal(payload);

          // Check if the response contains a reader that needs registration
          if (
            response.data &&
            response.data.reader &&
            response.data.reader.needsRegistration
          ) {
            return {
              success: false,
              needsRegistration: true,
              message: response.data.message || "Reader needs registration.",
            };
          }

          return { success: true, response };
        } catch (error) {
          retryCount++;

          if (retryCount <= maxRetries) {
            // Wait before retrying with exponential backoff
            const backoffTime = 1000 * Math.pow(2, retryCount - 1); // 1s, 2s
            await new Promise((r) => setTimeout(r, backoffTime));
          } else {
            return {
              success: false,
              error:
                error.message ||
                "Failed to display cart on terminal after multiple attempts",
            };
          }
        }
      }

      // This should never be reached due to the return statements above
      return { success: false, error: "Unexpected error in display operation" };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Unexpected error in display operation",
      };
    }
  },

  // Clear the reader while the page is closing or reloading. A normal
  // request is dropped then; a keepalive request is still sent. Same
  // endpoint, so a waiting payment is never touched.
  clearDisplayOnUnload: (readerId) => {
    try {
      const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
      if (!readerId || !token || typeof fetch !== "function") return false;
      fetch(`${API_CONFIG.BASE_URL}/terminal/readers/clear-display`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ readerId }),
      }).catch(() => {});
      return true;
    } catch (error) {
      return false;
    }
  },

  // Clear the cart shown on the reader (POST /terminal/readers/clear-display).
  // The server only clears a cart display; it never cancels a payment that
  // is waiting on the reader. One try: a failed clear leaves only a stale
  // cart on the screen, and the next display or clear replaces it.
  clearDisplay: async (readerId) => {
    if (!readerId) {
      return { success: false, error: "Reader ID is required" };
    }
    try {
      const { data } = await terminalAPI.clearReaderDisplay(readerId);
      return {
        success: Boolean(data && data.success),
        cleared: Boolean(data && data.cleared),
        skipped: Boolean(data && data.skipped),
        reason: data ? data.reason : undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to clear terminal display",
      };
    }
  },

  // Process payment with improved error handling and polling
  processPayment: async (paymentData) => {
    const {
      readerId,
      locationId,
      amount,
      currency = "usd",
      items,
      subtotal,
      taxAmount,
      taxRate = 0.086,
      description,
      customer,
      metadata = {},
      discountAmount,
      discountCode,
      // Issue #289: an earlier attempt of this sale; the server cancels it
      // first, so its card hold cannot stay open.
      replacePaymentIntentId,
    } = paymentData;

    try {
      // Step 1: Create a payment intent
      const paymentIntentResponse = await terminalAPI.createPaymentIntent({
        amount,
        currency,
        readerId,
        locationId,
        items,
        subtotal,
        taxAmount,
        taxRate,
        discountAmount,
        discountCode,
        replacePaymentIntentId,
        description:
          description ||
          generateSmartDescription(items, {
            subtotal,
            taxAmount,
            total: amount,
          }),

        customer,
        metadata,
      });
      // Check if the response contains a reader that needs registration
      if (
        paymentIntentResponse.data &&
        paymentIntentResponse.data.reader &&
        paymentIntentResponse.data.reader.needsRegistration
      ) {
        return {
          success: false,
          needsRegistration: true,
          message:
            paymentIntentResponse.data.message || "Reader needs registration.",
        };
      }

      // Store payment data
      const paymentIntentId = paymentIntentResponse.data.id;
      const clientSecret = paymentIntentResponse.data.client_secret;
      const transactionId = paymentIntentResponse.data.transaction;

      // Step 2: Send the payment to the reader. Issue #289: a reader error
      // is reported (it used to be ignored, and the screen waited for a
      // payment the reader never received). The server has already
      // cancelled the attempt in that case.
      const terminalPaymentResponse = await terminalAPI.createTerminalPayment({
        payment_intent_id: paymentIntentId,
        reader_id: readerId,
      });
      if (!terminalPaymentResponse.data.success) {
        return {
          success: false,
          paymentIntentId,
          error:
            terminalPaymentResponse.data.error ||
            "The payment could not be sent to the card reader. Try again.",
        };
      }

      return {
        success: true,
        paymentIntentId,
        clientSecret,
        transactionId,
      };
    } catch (error) {
      return {
        success: false,
        code: error.response?.data?.code,
        error:
          error.response?.data?.error ||
          error.message ||
          "Failed to process payment",
      };
    }
  },

  // Capture payment
  capturePayment: async (paymentIntentId, amount) => {
    if (!paymentIntentId) {
      return {
        success: false,
        error: "Payment intent ID is required for capture",
      };
    }

    if (!amount || amount <= 0) {
    }

    try {
      const captureResponse = await terminalAPI.capturePayment({
        payment_intent_id: paymentIntentId,
        amount_to_capture: amount,
      });

      return {
        success: true,
        data: captureResponse.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to capture payment",
      };
    }
  },

  // Issue #289 — watch a POS card payment until it has one clear state.
  //   succeeded -> the server has delivered the sale (once)
  //   failed    -> Stripe's reason (declined card, reader failure)
  //   canceled  -> cancelled, including "no longer on the card reader"
  //   pending   -> still waiting for the card. A slow tap is "waiting",
  //                never a decline (the old 50-second "declined" is gone).
  // The browser no longer captures payments: they capture automatically.
  // Returns { stop } so the caller can stop watching (for example when
  // staff cancel or retry).
  startPaymentStatusPolling: (paymentIntentId, _amount, onComplete, options = {}) => {
    const { intervalMs = 2000, onWaiting } = options;
    let stopped = false;
    let timer = null;
    const stop = () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
    if (!paymentIntentId) {
      if (onComplete) {
        onComplete({ success: false, status: "error", error: "Payment intent ID is required for polling" });
      }
      return { stop };
    }
    const startedAt = Date.now();
    const finish = (result) => {
      if (stopped) return;
      stop();
      if (onComplete) onComplete(result);
    };
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await terminalAPI.getPosPaymentStatus(paymentIntentId);
        if (stopped) return;
        if (data.status === "succeeded") {
          finish({
            success: true,
            status: "succeeded",
            data: {
              transaction: data.transaction,
              processResults: data.processResults || [],
              requiresPaymentMethod: Boolean(data.requiresPaymentMethod),
            },
          });
          return;
        }
        if (data.status === "failed") {
          finish({
            success: false,
            status: "failed",
            error: `The card payment failed: ${data.message || "declined"}. Try again or use another card.`,
          });
          return;
        }
        if (data.status === "canceled") {
          finish({
            success: false,
            status: "canceled",
            error: data.message || "The payment was cancelled.",
          });
          return;
        }
        if (onWaiting) {
          onWaiting({ elapsedMs: Date.now() - startedAt, message: data.message });
        }
      } catch (error) {
        // A short network problem: keep waiting.
      }
      if (!stopped) timer = setTimeout(poll, intervalMs);
    };
    poll();
    return { stop };
  },

  calculateProratedAmount: async (priceId, billingInterval) => {
    try {
      const response = await terminalAPI.calculateProratedAmount({
        priceId,
        billingInterval,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default terminalService;
